import cx from 'classnames';
import type { KeyboardEvent, VoidFunctionComponent } from 'react';

import { Badge, BadgeColors, BadgeSizes } from '../Badge';
import { type IconClassName, IconText } from '../Icon';
import Tooltip from '../Tooltip';

import styles from './Tabs.module.less';

export type TabProps = {
  /**
   * Note that we are explicitly asking for a label: string instead of children
   * as we are trying to add guardrails on this component/avoid unintentionally
   * passing anything as a child to Tab.
   */
  label: string;
  /**
   * This is used to "select" the tab. Note that this is usually a click event,
   * but it could be a key press or other event.
   */
  onSelect: () => void;
  tooltip?: string;
  icon?: IconClassName;
  isActive?: boolean;
  isDisabled?: boolean;
  badgeCount?: number;
};

/**
 * Tab represents a single "tab" in a set of tabs.
 */
const Tab: VoidFunctionComponent<TabProps> = ({
  label,
  onSelect,
  tooltip,
  icon,
  isActive,
  isDisabled,
  badgeCount,
}) => (
  <Tooltip id="tab-tooltip" tooltip={tooltip}>
    <li
      role="tab"
      className={cx(styles.tab, { [styles.tabActive]: isActive, [styles.tabDisabled]: isDisabled })}
      tabIndex={0}
      onKeyDown={(event: KeyboardEvent<HTMLLIElement>) => {
        if (!isActive && !isDisabled) {
          if (event.key === 'Enter' || event.key === ' ') {
            onSelect();
          }
        }
      }}
      onClick={() => !isActive && !isDisabled && onSelect()}
    >
      {icon ? (<IconText icon={icon} text={label} />) : label}
      {(badgeCount || badgeCount === 0) && (
        <span className={styles.badge}>
          <Badge
            color={BadgeColors.LightGray}
            size={BadgeSizes.Micro}
          >
            {badgeCount}
          </Badge>
        </span>
      )}
    </li>
  </Tooltip>
);

export default Tab;
